import { useCallback, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { deleteSegment as apiDeleteSegment, listSegments, upsertSegment } from '@/lib/api/product-segments'
import type { CustomSegment, ProductSegmentRule } from '@/components/v2/products/segments/segmentPresets'

const LEGACY_KEY = 'goki:product-segments'
const MIGRATED_KEY = 'goki:product-segments-migrated'

function loadLegacySegments(): CustomSegment[] {
  try {
    const raw = localStorage.getItem(LEGACY_KEY)
    const p = raw ? (JSON.parse(raw) as { customSegments?: CustomSegment[] }) : null
    return Array.isArray(p?.customSegments) ? p.customSegments : []
  } catch {
    return []
  }
}

/** Server-Persistenz + Einmal-Uebernahme lokaler Segmente (P2b/B8). Auswahl bleibt in useProductSegments. */
export function useProductSegmentsSync() {
  const qc = useQueryClient()
  const query = useQuery({ queryKey: ['product-segments'], queryFn: listSegments })
  const invalidate = () => qc.invalidateQueries({ queryKey: ['product-segments'] })
  const upsert = useMutation({ mutationFn: upsertSegment, onSuccess: invalidate })
  const del = useMutation({ mutationFn: apiDeleteSegment, onSuccess: invalidate })
  const segments = query.data?.segments ?? []

  useEffect(() => {
    if (!query.isSuccess || segments.length > 0 || localStorage.getItem(MIGRATED_KEY)) return
    const legacy = loadLegacySegments()
    if (legacy.length === 0) return
    localStorage.setItem(MIGRATED_KEY, '1')
    Promise.all(legacy.map((s) => upsertSegment({ name: s.name, color: s.color, rule: s.rule, pinned: s.pinned })))
      .then(() => {
        localStorage.removeItem(LEGACY_KEY)
        invalidate()
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isSuccess, segments.length])

  const find = useCallback((id: string) => segments.find((s) => s.id === id), [segments])
  const patch = (id: string, fields: Partial<Pick<CustomSegment, 'name' | 'color' | 'rule' | 'pinned'>>) => {
    const s = find(id)
    if (s) upsert.mutate({ id, name: s.name, color: s.color, rule: s.rule, pinned: s.pinned, ...fields })
  }

  return {
    customSegments: segments,
    addSegment: (seg: Omit<CustomSegment, 'id' | 'pinned'>) => upsert.mutate(seg),
    renameSegment: (id: string, name: string) => patch(id, { name }),
    recolorSegment: (id: string, color: string) => patch(id, { color }),
    setPinned: (id: string, pinned: boolean) => patch(id, { pinned }),
    updateRule: (id: string, rule: ProductSegmentRule) => patch(id, { rule }),
    deleteSegment: (id: string) => del.mutate(id),
  }
}
